import { MeasurementHash, MeasurementHashEntry } from 'app/stores/dictionary/useDictionaryMeasurements';
import { DirectionalMeasurementDimension, MeasurementDimension } from '@kentik/ui-shared/query/query.types';
import { isDirectionalDim, isDstDim, isSrcDim } from '@kentik/ui-shared/query/query';
import { checkMeasurement } from '@kentik/ui-shared/query/measurements';
import { addDirectionPrefixToLabel } from '@kentik/ui-shared/query/dimensionUtils';
import { isEnumItem } from '@kentik/ui-shared/query/enumUtils';

export interface DimensionMenuItem {
  id: string;
  label: string;
  className: string;
  isDirectionalNode: boolean;
  nodeData: MeasurementDimension & {
    selected?: boolean;
    selectedDirectionKey?: string;
    // Directions is already part of DirectionalMeasurementDimension when applicable
  };
}

export interface DimensionGroup {
  category: string;
  items: DimensionMenuItem[];
}

export function isDirectionalMenuItem(
  item: DimensionMenuItem
): item is DimensionMenuItem & { nodeData: DirectionalMeasurementDimension } {
  return item.isDirectionalNode;
}

function getGroupingKey(key: string, value: MeasurementDimension): string {
  if (value.Inverse) {
    return [key, value.Inverse].sort().join('|');
  }

  if (key.endsWith('_in') || key.endsWith('_out')) {
    return key.replace(/_in$|_out$/, '');
  }

  return key.replace(/_?(src|dst|input|output)_?/, '');
}

function sortDimensionItems(dimensions: DimensionMenuItem[]): DimensionMenuItem[] {
  return dimensions.toSorted((a, b) => {
    const labelA = a.label || a.id;
    const labelB = b.label || b.id;
    return labelA.localeCompare(labelB);
  });
}

function getMeasurementDimensions(measurementItem: MeasurementHashEntry): Record<string, MeasurementDimension> {
  const metrics = measurementItem?.storage?.Metrics ?? {};
  const dimensions = measurementItem?.storage?.Dimensions ?? {};
  const enumDimensions: Record<string, MeasurementDimension> = {};

  Object.entries(metrics).forEach(([metricName, metric]) => {
    if (isEnumItem(metric)) {
      enumDimensions[metricName] = { ...metric, Kind: 'enum' };
    }
  });

  return { ...dimensions, ...enumDimensions };
}

export function getGroupedDimensions(measurement: string, measurementHash: MeasurementHash): DimensionGroup[] {
  let dimensions = getMeasurementDimensions(measurementHash[measurement]);

  if (checkMeasurement.isTrafficRoot(measurement)) {
    // Add in all of the sub-measurements for traffic if the top-level option is selected
    // These may not have Category set. If so, add the sub-measurement name as the category.
    Object.keys(measurementHash)
      .filter((m) => checkMeasurement.isTrafficChild(m))
      .forEach((m) => {
        const extraDimensions = measurementHash[m]?.storage?.Dimensions ?? {};
        const categorized = Object.fromEntries(
          Object.entries(extraDimensions).map(([key, value]) => [
            key,
            value.Category ? value : { ...value, Category: measurementHash[m]?.displayName }
          ])
        );
        dimensions = { ...dimensions, ...categorized };
      });
  } else if (checkMeasurement.isTrafficChild(measurement)) {
    // As above, these sub-measurements in traffic may not have Category set, so fix those.
    const categorized = Object.fromEntries(
      Object.entries(dimensions).map(([key, value]) => [
        key,
        value.Category ? value : { ...value, Category: measurementHash[measurement]?.displayName }
      ])
    );

    // Now include the *shared* dimensions within the super-measurement /traffic
    const sharedTrafficDimensions = Object.fromEntries(
      Object.entries(measurementHash['/traffic']?.storage?.Dimensions ?? {}).filter(([, value]) => value.Shared)
    );

    dimensions = {
      ...sharedTrafficDimensions,
      ...categorized
    };
  } else if (checkMeasurement.isEventsChild(measurement) || checkMeasurement.isEventsRoot(measurement)) {
    const categorized = Object.fromEntries(
      Object.entries(dimensions).map(([key, value]) => [
        key,
        value.Category ? value : { ...value, Category: measurementHash[measurement]?.displayName }
      ])
    );
    dimensions = categorized;
  }

  // Now let's sort out directional stuff
  const groupedDirectionalDimensions = Object.entries(dimensions).reduce<
    Record<string, MeasurementDimension | DirectionalMeasurementDimension>
  >(
    (acc, [key, value]) => {
      const isNamespacedKey = key.startsWith('ktappprotocol__') || key.startsWith('ktsubtype__');
      if (value.Direction || (!isNamespacedKey && isDirectionalDim(key))) {
        let direction;
        if (value.Direction === 'src' || isSrcDim(key)) {
          direction = 'src';
        }
        if (value.Direction === 'dst' || isDstDim(key)) {
          direction = 'dst';
        }

        if (!direction) {
          // If it's not one of the directions we want, pretend it's not directional and move on
          acc[key] = value;
          return acc;
        }

        const groupingKey = getGroupingKey(key, value);
        // @ts-expect-error I'm checking for a property come on
        if (typeof acc[groupingKey]?.Directions === 'object') {
          const existing = acc[groupingKey] as DirectionalMeasurementDimension;
          // Merge SRC/DST into one entry
          acc[groupingKey] = {
            ...existing,
            Directions: {
              ...(existing.Directions || {}),
              [direction]: { ...value, key, Label: addDirectionPrefixToLabel(key, value.Label || '', true) }
            }
          } as DirectionalMeasurementDimension;
          return acc;
        }

        const sanitizedLabel = value?.Label?.replaceAll('Source', '').replaceAll('Destination', '').trim();
        const { Column: _Column, ...rest } = value;

        const directions: DirectionalMeasurementDimension['Directions'] = {
          [direction]: { ...value, key, Label: addDirectionPrefixToLabel(key, value.Label || '', true) }
        };

        return {
          ...acc,
          [groupingKey]: {
            ...rest,
            Label: sanitizedLabel,
            Directions: directions
          }
        };
      }

      acc[key] = value;
      return acc;
    },
    {} as Record<string, MeasurementDimension | DirectionalMeasurementDimension>
  );

  // Group this stuff by Category
  const groupMap: Record<string, DimensionMenuItem[]> = {};
  Object.entries(groupedDirectionalDimensions).forEach(([key, value]) => {
    const category = value.Category ?? 'Uncategorized';
    if (!groupMap[category]) {
      groupMap[category] = [];
    }

    groupMap[category].push({
      id: key,
      label: value.Label ?? key,
      className: 'dimension',
      isDirectionalNode: 'Directions' in value && !!value.Directions,
      nodeData: value
    });
  });

  return Object.entries(groupMap).map(([category, items]) => ({ category, items: sortDimensionItems(items) }));
}

export function markSelectedDimension(groups: DimensionGroup[], selectedDimensionName: string): DimensionGroup[] {
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      // For non-directional items, simple match
      if (!item.isDirectionalNode) {
        const selected = item.id === selectedDimensionName;
        if (item.nodeData.selected === selected) {
          return item;
        }
        return { ...item, nodeData: { ...item.nodeData, selected } };
      }

      // For directional items, check if either src or dst matches
      if (isDirectionalMenuItem(item)) {
        const directions = item.nodeData.Directions;
        const srcKey = directions?.src?.key;
        const dstKey = directions?.dst?.key;
        const isSelected = srcKey === selectedDimensionName || dstKey === selectedDimensionName;
        const selectedDirectionKey = isSelected ? selectedDimensionName : undefined;

        if (item.nodeData.selected === isSelected && item.nodeData.selectedDirectionKey === selectedDirectionKey) {
          return item;
        }

        return {
          ...item,
          nodeData: {
            ...item.nodeData,
            selected: isSelected,
            selectedDirectionKey
          }
        };
      }

      // Non-directional - should never match
      return item;
    })
  }));
}

export function getDimensionLabel(dimensionKey: string, groups: DimensionGroup[]): string {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.id === dimensionKey) {
        return item.label;
      }

      if (isDirectionalMenuItem(item)) {
        const directions = item.nodeData.Directions;
        if (directions?.src?.key === dimensionKey) {
          return directions.src.Label || item.label;
        }
        if (directions?.dst?.key === dimensionKey) {
          return directions.dst.Label || item.label;
        }
      }
    }
  }

  return dimensionKey;
}
